/**
 * Registry of pending WebSocket acknowledgements keyed by requestId.
 *
 * Every write toward the BFF is asynchronous: the HTTP response only carries a
 * `requestId`, the actual persistence happens later in the os-sinistri worker
 * (Kafka), which then publishes a notification relayed to the browser over the
 * WebSocket consumed by `ToastContainer`. Until that ack arrives the DB may still
 * hold the previous value, so a caller that needs "persisted for real" semantics
 * (e.g. saving the denuncia before releasing it as "Sinistro aperto", CSP-3425)
 * can register here and await the ack for its own requestId.
 *
 * The registry is deliberately framework-agnostic (no redux) so it can be used
 * from plain async flows and unit-tested in isolation.
 */

export const WS_ACK_OUTCOME = Object.freeze({
  SUCCESS: 'SUCCESS',
  FAILED: 'FAILED',
  TIMEOUT: 'TIMEOUT',
});

export const DEFAULT_WS_ACK_TIMEOUT_MS = 8000;

// How long an ack for a requestId nobody is waiting for yet is kept around, to
// cover the (unlikely) case where the WS message beats the HTTP response.
const EARLY_ACK_RETENTION_MS = 30000;

const pendingAcks = new Map();
const earlyAcks = new Map();

const toOutcome = (operation) => (
  operation?.status === 'SUCCESS' ? WS_ACK_OUTCOME.SUCCESS : WS_ACK_OUTCOME.FAILED
);

const buildAck = (requestId, operation) => ({
  requestId,
  outcome: toOutcome(operation),
  statusCode: operation?.statusCode ?? null,
});

/**
 * Waits for the WebSocket ack of the given requestId.
 * Never rejects: on timeout it resolves with `outcome: TIMEOUT` so the caller
 * decides whether to proceed or not.
 *
 * @param {string} requestId requestId returned by the BFF for the async write
 * @param {number} [timeoutMs]
 * @returns {Promise<{requestId: string, outcome: string, statusCode: (string|number|null)}>}
 */
export const waitForWsAck = (requestId, timeoutMs = DEFAULT_WS_ACK_TIMEOUT_MS) => new Promise((resolve) => {
  if (!requestId) {
    resolve({ requestId: null, outcome: WS_ACK_OUTCOME.TIMEOUT, statusCode: null });
    return;
  }
  const key = String(requestId);

  const early = earlyAcks.get(key);
  if (early) {
    clearTimeout(early.timer);
    earlyAcks.delete(key);
    resolve(early.ack);
    return;
  }

  const timer = setTimeout(() => {
    pendingAcks.delete(key);
    resolve({ requestId: key, outcome: WS_ACK_OUTCOME.TIMEOUT, statusCode: null });
  }, timeoutMs);

  pendingAcks.set(key, { resolve, timer });
});

/**
 * Feeds a WebSocket message into the registry. Resolves the pending waiter for
 * that requestId, or parks the ack for a short while if nobody is waiting yet.
 *
 * @param {string} requestId
 * @param {{status?: string, statusCode?: (string|number)}} operation
 * @returns {boolean} true if a pending waiter was resolved
 */
export const resolveWsAck = (requestId, operation) => {
  if (!requestId) {
    return false;
  }
  const key = String(requestId);
  const ack = buildAck(key, operation);

  const pending = pendingAcks.get(key);
  if (pending) {
    clearTimeout(pending.timer);
    pendingAcks.delete(key);
    pending.resolve(ack);
    return true;
  }

  const previousEarly = earlyAcks.get(key);
  if (previousEarly) {
    clearTimeout(previousEarly.timer);
  }
  const timer = setTimeout(() => earlyAcks.delete(key), EARLY_ACK_RETENTION_MS);
  earlyAcks.set(key, { ack, timer });
  return false;
};

export const hasPendingWsAck = (requestId) => pendingAcks.has(String(requestId));

/** Test helper: drops every pending waiter and parked ack. */
export const clearWsAckRegistry = () => {
  pendingAcks.forEach(({ timer }) => clearTimeout(timer));
  earlyAcks.forEach(({ timer }) => clearTimeout(timer));
  pendingAcks.clear();
  earlyAcks.clear();
};
